import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { LockReset } from "@mui/icons-material";
import axios from "axios";
import { ContentCopy } from "@mui/icons-material";
import { IconButton, InputAdornment } from "@mui/material";
import { useNavigate } from "react-router-dom";

const ProfilePage = ({ isLoggedIn }) => {
  const navigate = useNavigate(); 
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resettingKey, setResettingKey] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/"); // or any auth page you have
    }
    const fetchProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/profile`,
        );
        setProfile(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to load profile data");
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleResetApiKey = async () => {
    try {
      setResettingKey(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/profile/resetKey`,
        {},
      );
      setProfile((prev) => ({ ...prev, apikey: response.data.apikey }));
      setSuccess("API key reset successfully");
    } catch (err) {
      setError("Failed to reset API key");
    } finally {
      setResettingKey(false);
    }
  };

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">{error || "Profile not found"}</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{ py: 4, display: "flex", justifyContent: "center" }}
    >
      <Box sx={{ width: "100%" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Card elevation={3} sx={{ maxWidth: 500, margin: "0 auto" }}>
          <CardContent>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography variant="h4" component="h1">
                Profile Section
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Avatar
                src={profile.profile_pic}
                sx={{ width: 120, height: 120, mb: 2 }}
              />
              <Typography variant="h6">{profile.name}</Typography>
              <Typography color="text.secondary">{profile.email}</Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">API Key</Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={!resettingKey && <LockReset />}
                  onClick={handleResetApiKey}
                  disabled={resettingKey}
                >
                  {resettingKey ? <CircularProgress size={20} /> : "Reset Key"}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TextField
                value={profile.apikey || "No API key generated"}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                  endAdornment: profile.apikey ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(profile.apikey);
                          setSuccess("API key copied successfully");
                        }}
                        edge="end"
                      >
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Keep your API key secure. Reset it if compromised.
              </Typography>
            </Paper>

            {profile.account_type && (
              <Paper elevation={0} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Subscription Plan
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography>
                  <strong>Current Plan:</strong> {profile.account_type}
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ProfilePage;
